import clsx from 'clsx';
import { ArrowLeft, CheckCircle2, Search, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { isAdminName } from '../constants';
import { useStore } from '../store';

export function UserSelection() {
  const { statsCache, fetchOperators, onOperatorSelected, showDashboard, operator, isAdmin } =
    useStore();
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    void fetchOperators();
  }, [fetchOperators]);

  const operators = statsCache?.operators || [];
  const filteredOperators = operators.filter((op) => {
    if (isAdminName(op.name)) return false;
    return op.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <div className="mx-auto max-w-5xl space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Hero Section */}
      <div className="flex flex-col gap-2 border-b border-brand-border pb-6">
        <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
          Operator Access
        </h1>
        <p className="text-brand-text-secondary max-w-2xl">
          Select an existing profile to continue your session or create a new workspace alias.
        </p>
      </div>

      {(operator || isAdmin) && (
        <div className="flex justify-start">
          <button
            type="button"
            onClick={() => void showDashboard()}
            className="flex items-center gap-2 text-sm font-medium text-brand-text-secondary hover:text-brand-orange transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Dashboard
          </button>
        </div>
      )}

      {/* Existing Operators Grid */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h3 className="text-lg font-semibold text-brand-text-primary flex items-center gap-2">
            <User className="h-5 w-5 text-brand-text-secondary" />
            <span>Active Profiles</span>
          </h3>

          <div className="flex items-center gap-4">
            <div className="relative group">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-brand-text-secondary group-focus-within:text-brand-orange transition-colors" />
              <input
                type="text"
                placeholder="Search operators..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 w-64 rounded-lg border border-brand-border bg-brand-surface pl-8 pr-3 text-sm text-brand-text-primary focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange transition-all placeholder:text-brand-text-secondary/50"
              />
            </div>
            <span className="text-xs font-mono text-brand-text-secondary bg-brand-surface px-2 py-1 rounded border border-brand-border whitespace-nowrap">
              {filteredOperators.length} Matches
            </span>
          </div>
        </div>

        {filteredOperators.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredOperators.map((op) => {
              const isSpecialist = op.images >= 1000;
              return (
                <button
                  type="button"
                  key={op.name}
                  onClick={() => void onOperatorSelected(op.name)}
                  className="group relative flex cursor-pointer flex-col gap-3 rounded-xl border border-brand-border bg-brand-surface p-5 text-left transition-all hover:border-brand-orange/50 hover:bg-brand-surface-highlight hover:shadow-lg hover:-translate-y-1"
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="h-10 w-10 rounded-full bg-brand-dark border border-brand-border flex items-center justify-center text-sm font-bold text-brand-text-secondary group-hover:text-brand-orange group-hover:border-brand-orange/30 transition-colors">
                      {op.name.charAt(0).toUpperCase()}
                    </div>
                    {isSpecialist && <CheckCircle2 className="h-5 w-5 text-brand-orange" />}
                  </div>

                  <div>
                    <div className="font-semibold text-brand-text-primary group-hover:text-white transition-colors truncate">
                      {op.name}
                    </div>
                    <div className="text-xs text-brand-text-secondary mt-1 flex items-center gap-2">
                      <span
                        className={clsx(
                          'inline-block w-1.5 h-1.5 rounded-full',
                          isSpecialist ? 'bg-brand-orange' : 'bg-emerald-500'
                        )}
                      ></span>
                      {op.images.toLocaleString()} frames
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center p-12 rounded-xl border border-dashed border-brand-border bg-brand-surface/30 text-brand-text-secondary">
            <User className="h-8 w-8 mb-3 opacity-50" />
            <p>No operators found matching "{searchQuery}"</p>
          </div>
        )}
      </div>

      <div className="pt-4 border-t border-brand-border">
        <button
          type="button"
          onClick={() => void showDashboard()}
          className="flex items-center gap-2 text-sm font-medium text-brand-text-secondary hover:text-brand-orange transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </button>
      </div>
    </div>
  );
}
